import { t, te } from "@/language/translation";
import {
  createRouter,
  createWebHistory,
  type RouteLocationRaw,
  type RouteRecordRaw,
} from "vue-router";
import { isModuleActive } from "./functions/moduleConfig";
import store from "./store";
import { captureMessageInSentry } from "./sentry";
import { ENV } from "./ENV";
import { installUnsavedChangesNavigationGuard } from "./functions/useUnsavedChanges";
import { isIntern } from "./functions/environment";

const PageNotFound = () => import("./views/PageNotFound.vue");

const Roles = () => import("./views/RoleList.vue");
const RoleAssignment = () => import("./modules/organization/views/OGRoleAssignment.vue");

const DatapointTypes = () => import("./views/DatapointTypes.vue");
const DatapointType = () => import("./views/DatapointType.vue");
const BoardTemplates = () => import("./views/BoardTemplates.vue");
const BoardTemplate = () => import("./views/BoardTemplate.vue");

const SettingsColor = () => import("./views/systemSettings/SettingsColor.vue");
const GeneralSystemSettings = () => import("./views/systemSettings/GeneralSystemSettings.vue");
const DashLists = () => import("./views/Dashlist/Dashlists.vue");
const DashlistEditor = () => import("./views/Dashlist/DashlistEditor.vue");

const Changelog = () => import("./views/ChangelogView.vue");

const Redirect = () => import("./views/RedirectPage.vue");
const internalRoutes: RouteRecordRaw[] = isIntern()
  ? [
      {
        path: "/customerManagement",
        name: "customerManagement",
        component: () => import("./views/CustomerManagement.vue"),
      },
    ]
  : [];
// import routes from license
const licenseRouterAdditions: RouteRecordRaw[] = [];
const licenseRouters: Record<string, { default: RouteRecordRaw[] }> = import.meta.glob(
  "./license/**/router.ts",
  {
    eager: true,
  },
);

Object.entries(licenseRouters).forEach(([path, module]) => {
  if (isModuleActive(path.slice(2).split("/")[1])) {
    licenseRouterAdditions.push(...module.default);
  }
});

// import routes from modules
const modulesRouterAdditions: RouteRecordRaw[] = [];
const modulesRouters: Record<string, { default: RouteRecordRaw[] }> = import.meta.glob(
  "./modules/**/router.ts",
  {
    eager: true,
  },
);

Object.entries(modulesRouters).forEach(([_path, module]) => {
  modulesRouterAdditions.push(...module.default);
});

const routes: RouteRecordRaw[] = [
  {
    path: "/datapointTypes",
    name: "datapointTypes",
    component: DatapointTypes,
    meta: {
      rights: ["sqdc.admin"],
    },
  },
  {
    path: "/datapointType/:id",
    name: "datapointType",
    component: DatapointType,
    meta: {
      rights: ["sqdc.admin"],
    },
    props: (route) => ({ datapointTypeId: route.params.id }),
  },
  {
    path: "/boardTemplates",
    name: "boardTemplates",
    component: BoardTemplates,
    meta: {
      rights: ["sqdc.admin"],
    },
  },
  {
    path: "/boardTemplate/:id",
    name: "boardTemplate",
    component: BoardTemplate,
    meta: {
      rights: ["sqdc.admin"],
    },
    props: (route) => ({
      create: route.params.id === "new",
      boardTemplateId: route.params.id === "new" ? undefined : Number(route.params.id),
    }),
  },
  {
    path: "/roles",
    name: "roles",
    component: Roles,
    meta: {
      rights: ["accessRight.readWrite"],
    },
  },
  {
    path: "/roleAssignment",
    name: "roleAssignment",
    component: RoleAssignment,
    meta: {
      rights: ["user.role", "accessRight.readWrite"],
    },
    props: true,
  },
  {
    path: "/createRole",
    name: "RolesCreate",
    component: () => import("./views/RolesCreate.vue"),
    meta: {
      rights: ["accessRight.readWrite"],
    },
  },
  {
    path: "/updateRole/:id(\\d+)",
    name: "RolesUpdate",
    component: () => import("./views/RolesUpdate.vue"),
    meta: {
      rights: ["accessRight.readWrite"],
    },
    props: (route) => ({
      id: Number(route.params.id),
    }),
  },
  {
    path: "/dashlists",
    name: "dashlists",
    component: DashLists,
    meta: {
      rights: ["sqdc.admin"],
    },
  },
  {
    path: "/dashlists/create",
    name: "CreateDashlist",
    component: DashlistEditor,
    meta: {
      rights: ["sqdc.admin"],
    },
  },
  {
    path: "/dashlists/:id(\\d+)",
    name: "UpdateDashlist",
    component: DashlistEditor,
    meta: {
      rights: ["sqdc.admin"],
    },
    props: (route) => ({
      id: Number(route.params.id),
    }),
  },
  {
    path: "/settings",
    name: "settings",
    meta: {
      rights: ["systemSettings.write"],
    },
    children: [
      {
        path: "color",
        name: "settingsColor",
        meta: {
          rights: ["systemSettings.write"],
        },
        component: SettingsColor,
      },
      {
        path: "general",
        name: "generalSettings",
        meta: {
          rights: ["systemSettings.write"],
        },
        component: GeneralSystemSettings,
      },
    ],
  },
  {
    // Filler site for menu entries without a link
    path: "/dashboard",
    name: "dashboard",
    component: () => import("./modules/dashboard/views/DBDashboardView.vue"),
  },
  {
    path: "/changelog/:new",
    name: "ChangelogNew",
    component: Changelog,
  },
  {
    path: "/changelog",
    name: "Changelog",
    component: Changelog,
  },
  ...internalRoutes,
  ...licenseRouterAdditions,
  ...modulesRouterAdditions,
  {
    // Filler site for menu entries without a link
    path: "/redirect/:moduleName",
    name: "redirect",
    component: Redirect,
    props: (route) => ({ moduleName: route.params.moduleName }),
  },
  {
    path: "/:pathMatch(.*)*",
    name: "pageNotFound",
    component: PageNotFound,
    beforeEnter(to) {
      if (to.path === "/404") {
        return;
      }

      captureMessageInSentry(`routeNotFound - ${to.path}`, {
        level: "warning",
        tags: {
          route: to.path,
        },
        extra: {
          path: to.path,
          queryKeys: Object.keys(to.query),
        },
      });
    },
  },
];

const router = createRouter({
  history: createWebHistory("/"),
  routes,
});

installUnsavedChangesNavigationGuard(router);

const routerPush = router.push;
router.push = function push(to: RouteLocationRaw) {
  return routerPush(to).catch((err) => {
    throw err;
  });
};

const routerReplace = router.replace;
router.replace = function replace(to: RouteLocationRaw) {
  return routerReplace(to).catch((err) => {
    throw err;
  });
};

export default router;

router.afterEach((to, _from) => {
  const softwareName = [import.meta.env.DEV ? "DEV" : "LISA"];
  if (ENV.VITE_APP_BRAND_NAME === "EMMA") {
    softwareName[0] = "EMMA";
  }
  const routeName = to.name?.toString();
  const routeKey = `router.${routeName}`;
  if (te(routeKey) || import.meta.env.DEV) {
    softwareName.push(` | ${t(routeKey)}`);
  }
  document.title = softwareName.join("");
});

export type RouterAccessRightMetaType = {
  rights?: string[];
  accessRightSome?: boolean;
};

export function routerRightCheck(meta?: RouterAccessRightMetaType) {
  if (
    meta?.rights &&
    Array.isArray(meta?.rights) &&
    ((!meta?.accessRightSome &&
      !meta?.rights.every((right: string) => store.getters["login/hasAccessRight"](right))) ||
      (meta?.accessRightSome &&
        !meta?.rights.some((right: string) => store.getters["login/hasAccessRight"](right))))
  ) {
    return false;
  } else {
    return true;
  }
}

router.beforeEach((to, from) => {
  if (to.path === "/") {
    if (store.getters["login/initialPage"] != null && store.getters["login/initialPage"] !== "/") {
      return store.getters["login/initialPage"];
    }
    return true;
  }
  // Check if the user has the needed right to go to his destination
  const hasAccess = routerRightCheck(to.meta as RouterAccessRightMetaType);
  if (!hasAccess) {
    const rights = (to.meta as RouterAccessRightMetaType).rights ?? [];
    const missingRights = rights.filter((right) => !store.getters["login/hasAccessRight"](right));

    captureMessageInSentry(`routeAccessDenied - ${to.path}`, {
      level: "warning",
      tags: {
        route: to.path,
      },
      extra: {
        path: to.path,
        routeName: to.name?.toString(),
        requiredRights: rights,
        missingRights,
        accessRightSome: Boolean((to.meta as RouterAccessRightMetaType).accessRightSome),
      },
    });

    // If the user is missing a right he should be redirected to a "neutral" site
    return "/404";
  }
  if (!from.meta?.notSaveLastPage) {
    store.dispatch("login/updateLastPage", from.path);
  }
  return true;
});

function getAllRouteNames(routesProp: RouteRecordRaw[]): string[] {
  const names: string[] = [];

  const walk = (records: RouteRecordRaw[]) => {
    for (const r of records) {
      if (r.name != null && typeof r.name === "string") {
        names.push(r.name);
      }

      if (r.children?.length) {
        walk(r.children);
      }
    }
  };

  walk(routesProp);
  return names;
}

function compareNames(
  first: string[],
  second: string[],
): {
  missingInSecond: string[];
  additionalInSecond: string[];
} {
  const firstSet = new Set(first);
  const secondSet = new Set(second);

  const missingInSecond = first.filter((name) => !secondSet.has(name));
  const additionalInSecond = second.filter((name) => !firstSet.has(name));

  if (missingInSecond.length > 0) {
    // eslint-disable-next-line no-console
    console.log(
      "Missing router translations:",
      missingInSecond.map((n) => `t('router.${n}')`),
    );
  }
  if (additionalInSecond.length > 0) {
    // eslint-disable-next-line no-console
    console.log("Additional router translations (only trust if all licenses are active):", [
      additionalInSecond.map((n) => `./deleteL10nString.bash router ${n}`).join(" ; "),
    ]);
  }

  return { missingInSecond, additionalInSecond };
}

if (document.location.hostname === "localhost") {
  const names = getAllRouteNames(routes);
  void import("../translations/app/de.json").then(({ default: deTranslations }) => {
    const routerTranslations = Object.keys(deTranslations)
      .filter((key) => key.startsWith("router."))
      .map((key) => key.slice("router.".length));
    compareNames(names, routerTranslations);
  });
}
